import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { JsonService } from '../../_services/json.service';
import { LoggerService } from '../../_services/logger.service';
import { Country } from '../../_models/country.model';
import { ResumoBatchMes } from '../../_models/resumo-batch-mes.model';

@Component({
  selector: 'app-resumo-batch-mes',
  templateUrl: './resumo-batch-mes.component.html',
  styleUrls: ['./resumo-batch-mes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResumoBatchMesComponent implements OnInit {

  filtro = { countryCode: '', ano: String(new Date().getFullYear()), mes: '', dataInicial: '', dataFinal: '' };
  mostrarRiRm = false;

  listPaises: Country[] = [];

  readonly listAnos: string[] = Array.from(
    { length: new Date().getFullYear() - 2022 },
    (_, i) => String(new Date().getFullYear() - i)
  );

  readonly listMeses = [
    { label: 'Janeiro',   value: '01_ENERO' },
    { label: 'Fevereiro', value: '02_FEBRERO' },
    { label: 'Março',     value: '03_MARZO' },
    { label: 'Abril',     value: '04_ABRIL' },
    { label: 'Maio',      value: '05_MAYO' },
    { label: 'Junho',     value: '06_JUNIO' },
    { label: 'Julho',     value: '07_JULIO' },
    { label: 'Agosto',    value: '08_AGOSTO' },
    { label: 'Setembro',  value: '09_SEPTIEMBRE' },
    { label: 'Outubro',   value: '10_OCTUBRE' },
    { label: 'Novembro',  value: '11_NOVIEMBRE' },
    { label: 'Dezembro',  value: '12_DICIEMBRE' },
  ];

  dados: ResumoBatchMes[] = [];
  isLoading = false;
  semDados  = false;

  totais = this.totaisZerados();

  constructor(
    private jsonService: JsonService,
    private logger: LoggerService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.filtro.mes = this.listMeses[new Date().getMonth()].value;
    this.jsonService.listCountry().subscribe({
      next: paises => { this.listPaises = paises || []; this.cdr.markForCheck(); },
      error: erro  => { this.logger.error('Erro ao carregar países', erro); }
    });
  }

  buscar(): void {
    if (!this.filtro.countryCode || !this.filtro.ano || !this.filtro.mes) return;

    this.isLoading = true;
    this.semDados  = false;
    this.cdr.markForCheck();

    let params = new HttpParams()
      .set('countryCode', this.filtro.countryCode)
      .set('ano', this.filtro.ano)
      .set('mes', this.filtro.mes);

    const dataInicialIso = this.dataBrParaIso(this.filtro.dataInicial);
    const dataFinalIso   = this.dataBrParaIso(this.filtro.dataFinal);
    if (dataInicialIso) params = params.set('dataInicial', dataInicialIso);
    if (dataFinalIso)   params = params.set('dataFinal', dataFinalIso);

    this.jsonService.getDashboardResumoBatchMes(params).subscribe({
      next: data => {
        this.dados     = (data as ResumoBatchMes[]) || [];
        this.semDados  = this.dados.length === 0;
        this.calcularTotais();
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: erro => {
        this.logger.error('Erro ao carregar Reporte Consolidado', erro);
        this.semDados  = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  limpar(): void {
    this.filtro   = { countryCode: '', ano: String(new Date().getFullYear()), mes: this.listMeses[new Date().getMonth()].value, dataInicial: '', dataFinal: '' };
    this.dados    = [];
    this.semDados = false;
    this.totais   = this.totaisZerados();
    this.cdr.markForCheck();
  }

  // ── trackBy ──────────────────────────────────────────────────────────

  trackByRow(_: number, row: ResumoBatchMes): string {
    return row.fechaProceso + row.nroEnvio;
  }

  trackByCountryCode(_: number, item: Country): string {
    return item.countryCode;
  }

  trackByIndex(index: number): number {
    return index;
  }

  // ── Datas (dd/MM/yyyy) ─────────────────────────────────────────────────

  formatarDataInput(event: Event, campo: 'dataInicial' | 'dataFinal'): void {
    const input = event.target as HTMLInputElement;
    const digitos = input.value.replace(/\D/g, '').slice(0, 8);

    let formatado = digitos;
    if (digitos.length > 4)      formatado = `${digitos.slice(0, 2)}/${digitos.slice(2, 4)}/${digitos.slice(4)}`;
    else if (digitos.length > 2) formatado = `${digitos.slice(0, 2)}/${digitos.slice(2)}`;

    this.filtro[campo] = formatado;
    input.value = formatado;
  }

  private dataBrParaIso(dataBr: string): string | null {
    const m = (dataBr || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const [, dd, mm, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
  }

  formatEnvio(row: Pick<ResumoBatchMes, 'proveedor' | 'nroEnvio'>): string {
    return row.proveedor ? `${row.proveedor} ${row.nroEnvio}` : row.nroEnvio;
  }

  // ── Layout ───────────────────────────────────────────────────────────

  get colspanVazio(): number {
    return this.mostrarRiRm ? 21 : 13;
  }

  // ── Totais (usados no tfoot via cache e no CSV) ───────────────────────

  total(campo: keyof ResumoBatchMes): number {
    return this.dados.reduce((acc, r) => acc + (Number(r[campo]) || 0), 0);
  }

  pctTotal(numerador: keyof ResumoBatchMes, denominador: keyof ResumoBatchMes): number {
    const den = this.total(denominador);
    return den > 0 ? (this.total(numerador) / den) * 100 : 0;
  }

  // ── Exportação CSV ─────────────────────────────────────────────────

  exportarCsv(): void {
    const headers = [
      'FechaProceso', 'Envio',
      'TotalImagenes', 'ImagenesConError', 'NoSonRxs', 'ImagenesEnCola', 'ImagenesProcesadas',
      'ValidasAPPI', '%ValidasAPPI',
      ...(this.mostrarRiRm ? ['ValidasAPPI_RI', 'ValidasAPPI_RM'] : []),
      'ValidasDE', '%ValidasDE',
      ...(this.mostrarRiRm ? ['ValidasDE_RI', 'ValidasDE_RM'] : []),
      'PendientesRevision', '%Pendientes',
      ...(this.mostrarRiRm ? ['PendientesRevision_RI', 'PendientesRevision_RM', 'Total_RX_RI', 'Total_RX_RM'] : [])
    ];

    const linhas = this.dados.map(r => [
      r.fechaProceso, this.formatEnvio(r),
      r.totalImagenes, r.imagenesConError, r.noSonRxs, r.imagenesEnCola, r.imagenesProcesadas,
      r.validasAPPI, (r.imagenesProcesadas > 0 ? (r.validasAPPI / r.imagenesProcesadas * 100) : 0).toFixed(2),
      ...(this.mostrarRiRm ? [r.validasAPPI_RI, r.validasAPPI_RM] : []),
      r.validasDE, (r.imagenesProcesadas > 0 ? (r.validasDE / r.imagenesProcesadas * 100) : 0).toFixed(2),
      ...(this.mostrarRiRm ? [r.validasDE_RI, r.validasDE_RM] : []),
      r.pendientesRevision, (r.imagenesProcesadas > 0 ? (r.pendientesRevision / r.imagenesProcesadas * 100) : 0).toFixed(2),
      ...(this.mostrarRiRm ? [r.pendientesRevision_RI, r.pendientesRevision_RM, r.total_RX_RI, r.total_RX_RM] : [])
    ].join(';'));

    const csv = [headers.join(';'), ...linhas].join('\n');
    const aaaamm = `${this.filtro.ano}${this.filtro.mes.substring(0, 2)}`;
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url  = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const ts = new Date(); const stamp = `${ts.getFullYear()}${String(ts.getMonth()+1).padStart(2,'0')}${String(ts.getDate()).padStart(2,'0')}${String(ts.getHours()).padStart(2,'0')}${String(ts.getMinutes()).padStart(2,'0')}${String(ts.getSeconds()).padStart(2,'0')}`;
    link.setAttribute('download', `${aaaamm}–${this.filtro.countryCode}–consolidado_${stamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ── Internos ─────────────────────────────────────────────────────────

  private calcularTotais(): void {
    const sum = (campo: keyof ResumoBatchMes) =>
      this.dados.reduce((acc, r) => acc + (Number(r[campo]) || 0), 0);

    const imgProc = sum('imagenesProcesadas');

    this.totais = {
      totalImagenes:         sum('totalImagenes'),
      imagenesConError:      sum('imagenesConError'),
      noSonRxs:              sum('noSonRxs'),
      imagenesEnCola:        sum('imagenesEnCola'),
      imagenesProcesadas:    imgProc,
      total_RX_RI:           sum('total_RX_RI'),
      total_RX_RM:           sum('total_RX_RM'),
      validasAPPI:           sum('validasAPPI'),
      validasAPPI_RI:        sum('validasAPPI_RI'),
      validasAPPI_RM:        sum('validasAPPI_RM'),
      validasDE:             sum('validasDE'),
      validasDE_RI:          sum('validasDE_RI'),
      validasDE_RM:          sum('validasDE_RM'),
      pendientesRevision:    sum('pendientesRevision'),
      pendientesRevision_RI: sum('pendientesRevision_RI'),
      pendientesRevision_RM: sum('pendientesRevision_RM'),
      pctValidasAPPI:  imgProc > 0 ? sum('validasAPPI')       / imgProc * 100 : 0,
      pctValidasDE:    imgProc > 0 ? sum('validasDE')          / imgProc * 100 : 0,
      pctPendientes:   imgProc > 0 ? sum('pendientesRevision') / imgProc * 100 : 0,
    };
  }

  private totaisZerados() {
    return {
      totalImagenes: 0, imagenesConError: 0, noSonRxs: 0,
      imagenesEnCola: 0, imagenesProcesadas: 0,
      total_RX_RI: 0, total_RX_RM: 0,
      validasAPPI: 0, validasAPPI_RI: 0, validasAPPI_RM: 0,
      validasDE: 0, validasDE_RI: 0, validasDE_RM: 0,
      pendientesRevision: 0, pendientesRevision_RI: 0, pendientesRevision_RM: 0,
      pctValidasAPPI: 0, pctValidasDE: 0, pctPendientes: 0
    };
  }
}
